import React, { useState, useEffect, useCallback, useContext, useMemo } from "react";
import {
    Box,
    Button,
    Typography,
    Grid,
    TextField,
    Switch,
    Tooltip,
} from "@mui/material";
import { HelpOutline, RemoveCircleOutline } from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
// import { getContractTypeApi } from "../../../api/predefine.js"; 
import { generateTrackIDApi, addNewProjectApi } from "../../../api/project.js";
import { getAllUserApi } from "../../../api/user.js";
import { NotificationContext } from "../../../component/popup/NotificationProvider";
import { v4 as uuidv4 } from 'uuid';
import dayjs from "dayjs";
import SelectComponent from "../../../component/SelectComponent.js";
import Popup from "../../../component/popup/Popup.js";
import AddNewMemberContent from "./AddNewMemberContent.js";

const switchStyle = {
    width: 80,
    height: 40,
    "& .MuiSwitch-thumb": {
        width: "16px",
        height: "16px",
        mt: "3px",
        ml: "3px",
    },
    "& .Mui-checked": {
        transform: "translateX(42px)!important",
        color: "#fff!important",
    },
    "& .Mui-checked + .MuiSwitch-track": {
        backgroundColor: "#00A3E0 !important",
        opacity: "1 !important",
    },
};

const tooltipStyle = {
    minWidth: "600px",
    backgroundColor: "#E6E6E6",
    color: "#000",
    fontSize: "12px",
    p: 1,
    borderRadius: "4px",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
    whiteSpace: "pre-line",
};

const textFieldStyle = {
    my: 1,
    ml: 1,
    flex: 1,
    borderRadius: "4px",
    background: "#fff",
    "& .MuiInputBase-input": {
        py: 0.5,
        px: 0,
    },
    "& .MuiInputBase-root": {
        px: 2,
        py: 0,
    },
};

const datePickerStyle = {
    flex: 1,
    ml: 1,
    borderRadius: "4px",
    background: "#fff",
    "& .MuiInputBase-input": {
        pl: 2,
        pr: 0,
        py: 0.5,
    },
    "& .MuiIconButton-root.Mui-disabled": {
        color: "transparent!important",
    },
    "& .MuiOutlinedInput-input.Mui-disabled": {
        WebkitTextFillColor: "#000",
    },
    "& .Mui-disabled .MuiOutlinedInput-notchedOutline": {
        borderColor: "transparent!important",
    },
};

const boxStyle = {
    mx: 4,
    mt: 2,
    px: 2,
    pb: 2,
    pt: 1,
    background: "#D0D5DE",
    borderRadius: "10px",
};

const typographyStyle = {
    py: 1,
    fontWeight: 700,
    fontSize: "11px",
    minWidth: 140,
};

const buttonStyle = {
    borderRadius: "10px",
    lineHeight: "normal",
    my: 2,
};

const CreateProjectPopup = ({
    open,
    onClose,
    onSuccess,
    allUsers,
    allCompany,
    projectData
}) => {
    const { showSuccess, showError } = useContext(NotificationContext);
    const mainContactPerson_note = `Please note:\n\n The Project Main Contact Person (PMCP) acts as the primary liaison for the project, coordinating activities, managing resources, identifying risks, and maintaining documentation, while ensuring that all deliverables meet quality standards.`;
    const contractorPM_note = `Please note:\n\n Each contractor company must designate one Project PM. The Project PM is responsible for coordinating project activities, managing resources, identifying risks, and maintaining documentation, while ensuring that all deliverables meet quality standards.`;
    const Assai_note = `Please note:\n\n The “Client ID” can be found in the Assai Developer Portal or API Management section  after your register an account in Assai, which is a unique identifier for the application.\n
The “Secret Token” can be found in the Assai Developer Portal or API Management section after you register an account in Assai, which acts as a password for the application.Both values are essential for authenticating API requests.`;
    const ACC_note = `Please note:\n\n The “client ID” is essentially your application’s username and can be found in the My Apps section of the Autodesk DevPortal. It is a 32-character alphanumeric string (e.g., 5zw90va0UuwMKTnPS5sLsdgZjDkVYXN7). Your application passes it as the value for the client_id query parameter, and APS returns it in the client_id attribute in the returned JSON payload. \n
The “client secret” is essentially your application’s password and can be found next to the client ID in the My Apps section of the Autodesk DevPortal. It is a 16-character alphanumeric string (e.g., 7I6uN1rjneirxiMW), and your application passes it as the value for the client_secret query parameter.\n
The “revit urn (Uniform Resource Name)” is typically formatted as a string that includes the project ID and the specific model ID. For example, a Revit URN might look like urn:adsk.objects:os.object:myproject/myrevitmodel. It normally can be found in the Autodesk Forge viewer, Autodesk BIM 360, or API Responses.`;

    const renderLabel = (label) => (
        <Typography
            sx={{ ...typographyStyle, pt: "14px" }}
            dangerouslySetInnerHTML={{ __html: label }}
        />
    );

    const renderTitle = (label) => (
        <Typography
            sx={{
                py: 1,
                fontWeight: 700,
                fontSize: "12px",
                color: "#717273",
            }}
        >
            {label}
        </Typography>
    );

    const renderTextField = (
        label,
        field,
        placeholder,
        multiline = false,
        rows = 1,
        disabled = false,
        customOnChange,
        customOnBlur
    ) => {
        const handleChange = (e) => {
            if (customOnChange) {
                customOnChange(e.target.value);
            } else {
                updateProjectState(field, e.target.value);
            }
        };

        const handleBlur = (e) => {
            if (customOnBlur) {
                customOnBlur(e.target.value);
            }
        };

        return (
            <Grid item xs={6} sx={{ display: "flex", alignItems: "start" }}>
                {renderLabel(label)}
                <TextField
                    disabled={disabled}
                    value={projectState[field] || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={placeholder}
                    variant="outlined"
                    sx={textFieldStyle}
                    multiline={multiline}
                    rows={rows}
                    inputProps={{ maxLength: 200 }}
                />
            </Grid>
        );
    };

    const renderDatePicker = (label, field) => (
        <Grid item xs={6} sx={{ display: "flex", alignItems: "center" }}>
            <Typography component="span" sx={typographyStyle}>
                {label}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                    label=""
                    value={projectState[field]}
                    onChange={(date) => updateProjectState(field, date)}
                    format="DD/MM/YYYY"
                    inputFormat="DD/MM/YYYY"
                    renderInput={(params) => <TextField {...params} />}
                    sx={datePickerStyle}
                />
            </LocalizationProvider>
        </Grid>
    );

    const INITIAL_PROJECT_STATE = {
        projectName: "",
        projectNumber: "",
        startDate: null,
        endDate: null,
        description: "",
        location: "",
        members: [],
        mainContactPersonUserId: "",
        mainContactNameOptions: [],
        allUserOptions: [],
        contractors: [
            {
                id: "",
                companyId: "",
                // contractType: "",
                userId: "",
                contractorPMNameOptions: [],
            },
        ],
        contractorCompanyNameOptions: [],
        // categoryOptions: [],
        assiaClientId: "",
        assiaSecretToken: "",
        accClientId: "",
        accClientSecret: "",
        accRevitURN: "",
        assiaEnabled: false,
        accEnabled: false,
    };

    const [projectState, setProjectState] = useState(INITIAL_PROJECT_STATE);
    const [isCreating, setIsCreating] = useState(false);

    const isFormValid = () => {
        const hasStartDate = dayjs(projectState.startDate).isValid();
        const hasEndDate = dayjs(projectState.endDate).isValid();

        if (
            hasStartDate &&
            hasEndDate &&
            dayjs(projectState.endDate).isBefore(projectState.startDate)
        ) {
            return false;
        }
        const baseFieldsValid =
            projectState.projectName !== "" &&
            projectState.startDate !== "" &&
            projectState.endDate !== "" &&
            projectState.mainContactPersonUserId !== "";

        const contractorsValid = projectState.contractors.every((contractor) => {
            return (
                contractor.companyId !== "" &&
                // contractor.contractType !== "" &&
                contractor.userId !== ""
            );
        });

        const isProjectNameUnique = !projectData?.some(
            (project) =>
                project.projectName?.trim().toLowerCase() ===
                projectState.projectName?.trim().toLowerCase()
        );

        let isProjectNumberUnique = true;
        if (projectState.projectNumber && projectState.projectNumber.trim() !== "") {
            isProjectNumberUnique = !projectData?.some(
                (project) =>
                    project.projectNumber?.trim().toLowerCase() ===
                    projectState.projectNumber?.trim().toLowerCase()
            );
        }

        return (
            baseFieldsValid &&
            contractorsValid &&
            isProjectNameUnique &&
            isProjectNumberUnique
        );
    };

    const handleProjectNameChange = (value) => {
        setProjectState((prevState) => ({
            ...prevState,
            projectName: value,
        }));
    };

    const handleProjectNumberChange = (value) => {
        setProjectState((prevState) => ({
            ...prevState,
            projectNumber: value,
        }));
    };

    const handleProjectNameBlur = (value) => {
        if (!value.trim()) return;
        const normalize = (str) => str.replace(/\s+/g, "").toLowerCase();
        const isDuplicate = projectData?.some(
            (project) =>
                normalize(project.projectName || "") === normalize(value)
        );
        if (isDuplicate) {
            showError("Project name already exists. Please enter another project name before creating.");
        }
    };

    const handleProjectNumberBlur = (value) => {
        if (!value.trim()) return;
        const normalize = (str) => str.replace(/\s+/g, "").toLowerCase();
        const isDuplicate = projectData?.some(
            (project) =>
                normalize(project.projectNumber || "") === normalize(value)
        );
        if (isDuplicate) {
            showError("Project number already exists. Please enter another project number before creating.");
        }
    };

    const updateProjectState = (field, value) => {
        setProjectState((prevState) => ({
            ...prevState,
            [field]: value,
        }));
    };

    const createNewProject = async (event) => {
        event.preventDefault();
        if (isCreating) return;
        setIsCreating(true);
        const membersFormatter = projectState?.members.map((member) => ({
            userId: member.userId,
            accessLevel: member.accessLevel,
            editRight: member.editRight,
            reportModule: member.reportModule,
            costAccess: member.costAccess,
            isPM: false,
        }));

        const contractorFormatter = projectState?.contractors.map((contractor) => ({
            companyId: contractor.companyId,
            // contractType: contractor.contractType,
            userId: contractor.userId,
        }));

        try {
            // 每次都重新获取唯一trackId，防止复用
            const trackIdResult = await generateTrackIDApi();
            let trackId = trackIdResult?.data?.trackId;
            if (!trackId) {
                setIsCreating(false);
                showError("Failed to generate Track ID");
                return;
            }
            const params = {
                projectName: projectState.projectName,
                projectNumber: projectState.projectNumber,
                startDate: dayjs(projectState.startDate).format("YYYY-MM-DD"),
                endDate: dayjs(projectState.endDate).format("YYYY-MM-DD"),
                description: projectState.description,
                location: projectState.location,
                mainContactPersonUserId: projectState.mainContactPersonUserId,
                contractors: contractorFormatter || [],
                members: membersFormatter || [],
                assiaEnabled: projectState.assiaEnabled,
                assiaClientId: projectState.assiaClientId,
                assiaSecretToken: projectState.assiaSecretToken,
                accEnabled: projectState.accEnabled,
                accClientId: projectState.accClientId,
                accClientSecret: projectState.accClientSecret,
                accRevitURN: projectState.accRevitURN,
                trackId: trackId,
            };
            const result = await addNewProjectApi(params);
            if (result.data) {
                onSuccess();
                showSuccess("New project has been successfully created.");
                setProjectState(INITIAL_PROJECT_STATE);
                setIsCreating(false);
                onClose();
            } else {
                setIsCreating(false);
            }
        } catch {
            setIsCreating(false);
            showError("Creation failed");
        }
    };

    const getCLPPersonData = async (CLPId) => {
        try {
            const result = await getAllUserApi(CLPId);
            if (result?.data) {
                const filteredData = result.data.filter(
                    (item) => item.status !== "Disable"
                );
                const formattedMembers = filteredData?.map((item) => ({
                    ...item,
                    value: item.userId,
                    label: `${item.firstName} ${item.lastName}`,
                }));
                updateProjectState("mainContactNameOptions", formattedMembers);
            }
        } catch {
            showError("Received main contact person data occurred error.");
        }
    };

    // const getContractTypeData = async () => {
    //     try {
    //         const result = await getContractTypeApi();
    //         if (result.data) {
    //             updateProjectState("categoryOptions", result.data);
    //         }
    //     } catch {
    //         showError("Received contract type data occurred error.");
    //     }
    // };

    const getExcludedUserIds = () => {
        const mainContactPersonId = projectState.mainContactPersonUserId;
        const contractorPmIds = projectState.contractors.map(contractor => contractor.userId).filter(userId => userId);
        const excludedIds = [...new Set([mainContactPersonId, ...contractorPmIds].filter(id => id))];
        return excludedIds;
    };

    const filteredUserOptions = useMemo(() => {
        if (!projectState.allUserOptions) return [];
        const excludedIds = getExcludedUserIds();
        return projectState.allUserOptions
            .filter(option => !excludedIds.includes(option.value))
            .sort((a, b) => {
                const labelA = a.label?.trim().charAt(0).toUpperCase() || '';
                const labelB = b.label?.trim().charAt(0).toUpperCase() || '';

                if (!labelA) return 1;
                if (!labelB) return -1;

                return labelA.localeCompare(labelB);
            });
    }, [
        projectState.allUserOptions,
        projectState.mainContactPersonUserId,
        projectState.contractors,
    ]);

    const conpanyNameChange = async (value, item) => {
        try {
            const result = await getAllUserApi(value);
            if (result?.data) {
                const filteredData = result.data.filter(
                    (item) => item.status !== "Disable"
                );
                const formattedData = filteredData?.map((item) => ({
                    ...item,
                    value: item.userId,
                    label: `${item.firstName} ${item.lastName}`,
                }));
                setProjectState((prevState) => ({
                    ...prevState,
                    contractors: prevState.contractors.map((f) =>
                        f.id === item.id
                            ? {
                                ...f,
                                companyId: value,
                                contractorPMNameOptions: formattedData,
                            }
                            : f
                    ),
                }));
            }
        } catch {
            showError("Received user data occurred error.");
        }
    };

    const contractorPMNameChange = (value, item) => {
        setProjectState((prevState) => ({
            ...prevState,
            contractors: prevState.contractors.map((f) =>
                f.id === item.id ? { ...f, userId: value } : f
            ),
        }));
    };

    const addContractorPM = () => {
        setProjectState((prevState) => ({
            ...prevState,
            contractors: [
                ...prevState.contractors,
                {
                    id: uuidv4(),
                    companyId: "",
                    // contractType: "",
                    userId: "",
                    contractorPMNameOptions: [],
                },
            ],
        }));
    };

    const receiveMemberData = useCallback((newMembers) => {
        updateProjectState("members", newMembers);
    }, []);

    useEffect(() => {
        if (projectState.startDate && projectState.endDate) {
            if (dayjs(projectState.endDate).isBefore(projectState.startDate)) {
                showError("The End Date cannot be earlier than the Start Date.");
            }
        }
    }, [projectState.startDate, projectState.endDate]);

    useEffect(() => {
        if (open) {
            setProjectState({ ...INITIAL_PROJECT_STATE });
        }
        if (!allUsers || !allCompany) return;
        const filteredData = allUsers.filter((item) => item.status !== "Disable");
        const formatted = filteredData.map((item) => ({
            ...item,
            value: item.userId,
            label: `${item.firstName} ${item.lastName}`,
        }));
        const CLPId = allCompany.find((item) => item.label === "CLP").value;
        updateProjectState("contractorCompanyNameOptions", allCompany);
        updateProjectState("allUserOptions", formatted);
        // getContractTypeData();
        getCLPPersonData(CLPId);
    }, [allUsers, allCompany, open]);

    return (
        <>
            <Popup
                open={open}
                onClose={onClose}
                popupStyle={{
                    minWidth: 1000,
                    maxWidth: 1000,
                }}
                modalStyle={{
                    "& .MuiBox-root.css-ft9d2q": {
                        borderRadius: "10px 10px 0 0",
                    },
                }}
                title="Project Creation"
            >
                <Box maxHeight="calc(100vh - 160px)" overflow="auto">
                    <form onSubmit={createNewProject}>
                        <Box sx={boxStyle}>
                            {renderTitle("Project Details")}
                            <Grid container columnSpacing={3}>
                                {renderTextField(
                                    "* Project Name:",
                                    "projectName",
                                    "Please Enter Project Name...", false, 1, false, handleProjectNameChange, handleProjectNameBlur
                                )}
                                {renderDatePicker("* Planned Start Date:", "startDate")}
                                {renderTextField(
                                    "&nbsp;&nbsp;&nbsp;Project Number:",
                                    "projectNumber",
                                    "Please Enter Project Number...", false, 1, false, handleProjectNumberChange, handleProjectNumberBlur
                                )}
                                {renderDatePicker("* Planned End Date:", "endDate")}
                                {renderTextField(
                                    "&nbsp;&nbsp;&nbsp;Project Summary:",
                                    "description",
                                    "Please Enter Project Summary...",
                                    true,
                                    4
                                )}
                                {renderTextField(
                                    "&nbsp;&nbsp;&nbsp;Project Location:",
                                    "location",
                                    "Please Enter Project Location..."
                                )}
                            </Grid>
                        </Box>
                        <Box sx={boxStyle}>
                            <Grid container>
                                <Grid item>{renderTitle("Project Main Contact Person")}</Grid>
                                <Grid item xs={2} container alignItems="center" marginLeft={1}>
                                    <Tooltip
                                        title={mainContactPerson_note}
                                        placement="top"
                                        componentsProps={{
                                            tooltip: {
                                                sx: tooltipStyle,
                                            },
                                        }}
                                    >
                                        <HelpOutline sx={{ color: "#717273", cursor: "pointer" }} />
                                    </Tooltip>
                                </Grid>
                            </Grid>
                            <Grid container columnSpacing={3}>
                                <Grid item xs={6} display="flex" alignItems="center">
                                    <SelectComponent
                                        options={projectState.mainContactNameOptions}
                                        value={projectState.mainContactPersonUserId}
                                        onChange={(value) => {
                                            updateProjectState("mainContactPersonUserId", value);
                                        }}
                                        selectLabel="* Name:"
                                        labelStyle={{ fontSize: "11px", minWidth: 140, mr: 1 }}
                                        selectStyle={{ width: "100%" }}
                                        placeholder="Please Select Name..."
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                        <Box sx={boxStyle}>
                            <Grid container>
                                <Grid item xs={2}>
                                    {renderTitle("Project Contractor PM")}
                                </Grid>
                                <Grid item xs={9} container alignItems="center">
                                    <Tooltip
                                        title={contractorPM_note}
                                        placement="top"
                                        componentsProps={{
                                            tooltip: {
                                                sx: tooltipStyle,
                                            },
                                        }}
                                    >
                                        <HelpOutline sx={{ color: "#717273", cursor: "pointer" }} />
                                    </Tooltip>
                                </Grid>
                                <Grid
                                    item
                                    xs={1}
                                    sx={{ maxWidth: "20px!important", ml: "53px" }}
                                    container
                                    alignItems="center"
                                >
                                    <img
                                        onClick={addContractorPM}
                                        src="assets/buttons/add.svg"
                                        alt="add"
                                        style={{ cursor: "pointer", width: "18px", height: "18px" }}
                                    ></img>
                                </Grid>
                            </Grid>
                            <Grid container columnSpacing={3}>
                                {projectState.contractors.map((item) => {
                                    const selectedCompanyIds = projectState.contractors
                                        .filter(c => c.companyId && c.id !== item.id)
                                        .map(c => c.companyId);
                                    const filteredCompanyOptions = projectState.contractorCompanyNameOptions?.filter(
                                        (option) =>
                                            option.label !== "CLP" &&
                                            !selectedCompanyIds.includes(option.value)
                                    );
                                    return (
                                        <React.Fragment key={item.id}>
                                            <Grid item xs={5.7} display="flex" alignItems="center">
                                                <SelectComponent
                                                    options={filteredCompanyOptions}
                                                    // options={projectState.contractorCompanyNameOptions?.filter((item) => item.label !== "CLP")}
                                                    value={item.companyId}
                                                    onChange={(value) => conpanyNameChange(value, item)}
                                                    selectLabel="* Company Name:"
                                                    labelStyle={{ fontSize: "11px", minWidth: 140, mr: 1 }}
                                                    selectStyle={{ width: "100%" }}
                                                    placeholder="Please Select Company Name..."
                                                />
                                            </Grid>
                                            {/* <Grid item xs={6} display="flex" alignItems="center">
                                            <SelectComponent
                                                options={projectState.categoryOptions}
                                                value={item.contractType}
                                                onChange={(value) => {
                                                    setProjectState((prevState) => ({
                                                        ...prevState,
                                                        contractors: prevState.contractors.map((info) =>
                                                            info.id === item.id
                                                                ? { ...info, contractType: value }
                                                                : info
                                                        ),
                                                    }));
                                                }}
                                                selectLabel="* Category:"
                                                labelStyle={{ fontSize: "11px", minWidth: 140, mr: 1 }}
                                                selectStyle={{ width: "100%" }}
                                                placeholder="Please Select Category..."
                                            />
                                        </Grid> */}
                                            <Grid item xs={5.7} display="flex" alignItems="center">
                                                <SelectComponent
                                                    options={item.contractorPMNameOptions}
                                                    value={item.userId}
                                                    onChange={(event) =>
                                                        contractorPMNameChange(event, item)
                                                    }
                                                    selectLabel="* Contractor PM Name:"
                                                    labelStyle={{ fontSize: "11px", minWidth: 140, mr: 1 }}
                                                    selectStyle={{ width: "100%" }}
                                                    placeholder="Please Select PM Name..."
                                                />
                                            </Grid>
                                            <Grid item xs={0.6} display="flex" alignItems="center">
                                                <RemoveCircleOutline
                                                    onClick={() => {
                                                        setProjectState((prevState) => {
                                                            if (prevState.contractors.length <= 1) {
                                                                return prevState;
                                                            }

                                                            return {
                                                                ...prevState,
                                                                contractors: prevState.contractors.filter(
                                                                    (contractor) => contractor.id !== item.id
                                                                ),
                                                            };
                                                        });
                                                    }}
                                                    sx={{
                                                        cursor:
                                                            projectState.contractors.length > 1
                                                                ? "pointer"
                                                                : "not-allowed",
                                                        color: "#FF0000",
                                                        fontSize: 21
                                                    }}
                                                />
                                            </Grid>
                                        </React.Fragment>
                                    );
                                })}
                            </Grid>
                        </Box>
                        <Box sx={boxStyle}>
                            {renderTitle("Project Members")}
                            <AddNewMemberContent
                                options={filteredUserOptions}
                                // options={projectState.allUserOptions.filter(option => !getExcludedUserIds().includes(option.value))}
                                onCreate={receiveMemberData}
                                onSetError={(value) => {
                                    showError(value);
                                }}
                            />
                        </Box>
                        <Box sx={boxStyle}>
                            <Grid container alignItems="center">
                                <Grid item xs={2}>
                                    {renderTitle("Project Upload to Assai:")}
                                </Grid>
                                <Grid item xs={2} container alignItems="center">
                                    <Box position="relative">
                                        {projectState.assiaEnabled ? (
                                            <Typography
                                                sx={{
                                                    position: "absolute",
                                                    top: "11px",
                                                    left: "28px",
                                                    color: "#fff",
                                                    zIndex: 2,
                                                }}
                                            >
                                                Yes
                                            </Typography>
                                        ) : (
                                            <Typography
                                                sx={{
                                                    position: "absolute",
                                                    top: "11px",
                                                    left: "36px",
                                                    color: "#fff",
                                                    zIndex: 2,
                                                }}
                                            >
                                                No
                                            </Typography>
                                        )}
                                        <Switch
                                            checked={projectState.assiaEnabled}
                                            onChange={() => {
                                                updateProjectState(
                                                    "assiaEnabled",
                                                    !projectState.assiaEnabled
                                                );
                                            }}
                                            name="assiaEnabled"
                                            color="primary"
                                            sx={switchStyle}
                                        />
                                    </Box>
                                    <Tooltip
                                        title={Assai_note}
                                        placement="top"
                                        componentsProps={{
                                            tooltip: {
                                                sx: tooltipStyle,
                                            },
                                        }}
                                    >
                                        <HelpOutline sx={{ color: "#717273", cursor: "pointer" }} />
                                    </Tooltip>
                                </Grid>
                            </Grid>
                            <Grid container columnSpacing={3}>
                                {renderTextField(
                                    "Client ID:",
                                    "assiaClientId",
                                    "Please Enter Client ID...",
                                    null,
                                    null,
                                    false
                                )}
                                <Grid item xs={6}></Grid>
                                {renderTextField(
                                    "Secret Token:",
                                    "assiaSecretToken",
                                    "Please Enter Secret Token...",
                                    null,
                                    null,
                                    false
                                )}
                            </Grid>
                        </Box>
                        <Box sx={boxStyle}>
                            <Grid container alignItems="center">
                                <Grid item xs={2}>
                                    {renderTitle("ACC:")}
                                </Grid>
                                <Grid item xs={2} container alignItems="center">
                                    <Box position="relative">
                                        {projectState.accEnabled ? (
                                            <Typography
                                                sx={{
                                                    position: "absolute",
                                                    top: "11px",
                                                    left: "28px",
                                                    color: "#fff",
                                                    zIndex: 2,
                                                }}
                                            >
                                                Yes
                                            </Typography>
                                        ) : (
                                            <Typography
                                                sx={{
                                                    position: "absolute",
                                                    top: "11px",
                                                    left: "36px",
                                                    color: "#fff",
                                                    zIndex: 2,
                                                }}
                                            >
                                                No
                                            </Typography>
                                        )}
                                        <Switch
                                            checked={projectState.accEnabled}
                                            onChange={() => {
                                                updateProjectState(
                                                    "accEnabled",
                                                    !projectState.accEnabled
                                                );
                                            }}
                                            name="accEnabled"
                                            color="primary"
                                            sx={switchStyle}
                                        />
                                    </Box>
                                    <Tooltip
                                        title={ACC_note}
                                        placement="top"
                                        componentsProps={{
                                            tooltip: {
                                                sx: tooltipStyle,
                                            },
                                        }}
                                    >
                                        <HelpOutline sx={{ color: "#717273", cursor: "pointer" }} />
                                    </Tooltip>
                                </Grid>
                            </Grid>
                            <Grid container columnSpacing={3}>
                                {renderTextField(
                                    "Client ID:",
                                    "accClientId",
                                    "Please Enter Client ID...",
                                    null,
                                    null,
                                    false
                                )}
                                <Grid item xs={6}></Grid>
                                {renderTextField(
                                    "Client Secret:",
                                    "accClientSecret",
                                    "Please Enter Client Secret...",
                                    null,
                                    null,
                                    false
                                )}
                                <Grid item xs={6}></Grid>
                                {renderTextField(
                                    "Revit Urn:",
                                    "accRevitURN",
                                    "Please Enter Revit Urn...",
                                    null,
                                    null,
                                    false
                                )}
                            </Grid>
                        </Box>
                    </form>
                </Box>
                <Box textAlign="center">
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={!isFormValid() || isCreating}
                        color={isCreating ? "inherit" : (isFormValid() ? "success" : "primary")}
                        sx={buttonStyle}
                        onClick={createNewProject}
                    >
                        {isCreating ? "Creating..." : "Create"}
                    </Button>
                </Box>
            </Popup>
        </>
    );
};

export default CreateProjectPopup;
